import { useEffect, useState } from 'react';

import axios from 'axios';
import FooterJSON from '../../../mock/footer.json';
import {
  FooterData,
  FooterLinksList,
  FooterSocialItem,
} from '../../../types/ee-footer';
import { ICON_SOCIAL_MAP } from '../../atoms/SVG';
import { useDeviceType } from '../../../hooks';
import EEBFooterDesktop from './EEBFooterDesktop';
import EEBFooterSmallDevice from './EEBFooterSmallDevice';
import { useEnvVariable } from '../../../contexts/EnvironmentContext';
import { stripAemPrefixFromObject } from '../../../utils';

const EEBFooter = (): React.ReactElement => {
  const { isTablet, isMobile } = useDeviceType();

  const { viteNextPublicEeFooterUrl } = useEnvVariable();

  const [footerData, setFooterData] = useState<FooterData | null>(null);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const { data } = await axios.get(viteNextPublicEeFooterUrl);
        if (data && typeof data === 'object') {
          setFooterData(stripAemPrefixFromObject(data));
        } else {
          throw new Error('Invalid response format');
        }
      } catch (error) {
        console.error('Error fetching footer data:', error);
        // Fallback to mock data
        setFooterData(null);
      }
    };

    if (viteNextPublicEeFooterUrl) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const footerGlobal =
    footerData?.[':items']?.ee_global_footer ||
    stripAemPrefixFromObject(FooterJSON)?.[':items']?.ee_global_footer;
  const footerRoot =
    footerGlobal?.eeGlobalFooterRoot?.[':items']?.['ee-global-footer-root']?.[
      ':items'
    ];

  // Helper to get first links list from a responsive grid (key names vary between environments)
  const getFirstLinksList = (
    grid: Record<string, unknown> | undefined,
  ): FooterLinksList | undefined => {
    const items = grid?.[':items'] as Record<string, FooterLinksList> | undefined;
    if (!items) return undefined;
    const firstKey = Object.keys(items).find((key) => key.startsWith('links_list'));
    return firstKey ? items[firstKey] : undefined;
  };

  const itemGroups = [
    getFirstLinksList(footerRoot?.responsivegrid),
    getFirstLinksList(footerRoot?.responsivegrid_1),
    getFirstLinksList(footerRoot?.responsivegrid_2),
    getFirstLinksList(footerRoot?.responsivegrid_3),
  ].filter(Boolean) as FooterLinksList[];

  const socialData = footerRoot?.social_links;
  const socialLinks =
    socialData?.socialItems?.map((item: FooterSocialItem) => {
      const iconKey = item.altText?.toLowerCase() ?? '';
      return {
        altLabel: item.altText,
        ariaLabel: `${item.altText} Link`,
        linkUrl: item.linkUrl,
        icon: ICON_SOCIAL_MAP[iconKey] || undefined,
      };
    }) || [];

  const infoLinks = footerGlobal?.infoLinks || [];
  const copyrightNotice = footerGlobal?.copyrightNotice;

  const props = {
    itemGroups,
    socialLinks,
    infoLinks,
    copyrightNotice,
    socialData,
    isMobile,
    isTablet,
  };

  const getFooterComponent = () => {
    if (isTablet || isMobile) return <EEBFooterSmallDevice {...props} />;
    return <EEBFooterDesktop {...props} />;
  };

  return <div id="site-footer">{getFooterComponent()}</div>;
};

export default EEBFooter;
